import React, { useEffect, useState } from 'react';
import { Routes, Route, useParams, useLocation } from 'react-router-dom';
import Homepage from './components/Homepage';
import CategoryPage from './components/CategoryPage';
import MoviesPage from './components/MoviesPage';
import SeriesPage from './components/SeriesPage';
import HomepageRedirect from './components/HomepageRedirect';
import MoviePDP from './components/MoviePDP';
import SeriesPDP from './components/SeriesPDP';
import LiveTVPage from './components/LiveTVPage';
import NotFoundPage from './components/NotFoundPage';
import Landing from './components/Landing';
import StreamingDashboard from './components/StreamingDashboard';
import CustomLoginPage from './components/auth/CustomLoginPage';
import AuthWrapper from './components/auth/AuthWrapper';
import ProfileRedirectHandler from './components/auth/ProfileRedirectHandler';
import LandingRedirectHandler from './components/auth/LandingRedirectHandler';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MyListsPage from './pages/MyListsPage';
import ProfileSelectionPage from './pages/ProfileSelectionPage';
import { AppLoadProvider } from './contexts/AppLoadContext';
import MobileIncompatiblePage from './components/MobileIncompatiblePage';
import SmartAppBanner from './components/SmartAppBanner';
import DeviceDetection from './utils/DeviceDetection';

import { ProfileProvider } from './contexts/ProfileContext';
import SearchPage from './components/search/SearchPage';
import ContentRouter from './components/ContentRouter';
import TrailerWatchPage from './components/TrailerWatchPage';
import AppleAppSiteAssociation from './components/AppleAppSiteAssociation';
import AndroidAssetLinks from './components/AndroidAssetLinks';
import AdsTxt from './components/AdsTxt';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfUsePage from './components/TermsOfUsePage';
import CCPAPage from './components/CCPAPage';
import DoNotSellPage from './components/DoNotSellPage';
import EmailConsentPage from './components/EmailConsentPage';
import VibeCodingStoryPage from './pages/VibeCodingStoryPage';
import DevicesPage from './pages/DevicesPage';
import CheckoutPage from './components/CheckoutPage';
import PaymentProfilePage from './pages/PaymentProfilePage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import { useStreamAuth } from './hooks/useStreamAuth';
import useAuthUrlCleanup from './hooks/useAuthUrlCleanup';

const MoviePDPWrapper = () => {
  const { movieId, videoSlug } = useParams();
  const id = movieId || videoSlug;
  return <MoviePDP movieId={id} />;
};

const SeriesPDPWrapper = () => {
  const { seriesId, videoSlug } = useParams();
  const id = seriesId || videoSlug;
  return <SeriesPDP seriesId={id} />;
};

const CategoryPageWrapper = () => {
  const { categorySlug } = useParams();
  return <CategoryPage categorySlug={categorySlug} />;
};

const WatchPageWrapper = () => {
  return <ContentRouter />;
};

const SearchPageWrapper = () => {
  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <SearchPage />
    </React.Suspense>
  );
};

const MyStreamsWrapper = () => {
  return (
    <div className="min-h-screen p-6 text-white bg-gray-900">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-3xl font-bold">My Streams</h1>
        <p className="text-gray-300">Your personal streaming content will appear here.</p>
      </div>
    </div>
  );
};

const CreateStreamWrapper = () => {
  return (
    <div className="min-h-screen p-6 text-white bg-gray-900">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-3xl font-bold">Create Stream</h1>
        <p className="text-gray-300">Stream creation interface coming soon...</p>
      </div>
    </div>
  );
};

function App() {
  // Get current location for deep linking
  const location = useLocation();
  const { logout } = useStreamAuth();

  // Automatically clean Auth0 callback parameters
  useAuthUrlCleanup({
    cleanOnMount: true,
    cleanOnRouteChange: true,
    delay: 100
  });

  // Handle SpotlightService logout events
  useEffect(() => {
    const handleSpotlightLogout = event => {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    };

    window.addEventListener('spotlightServiceLogout', handleSpotlightLogout);

    return () => {
      window.removeEventListener('spotlightServiceLogout', handleSpotlightLogout);
    };
  }, [logout]);

  // Scroll to top on route change
  // Skip for live-tv channel switches — same page, just channel change
  useEffect(() => {
    if (location.pathname.startsWith('/live-tv/')) return;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Device detection state
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(false);
  const [showSmartBanner, setShowSmartBanner] = useState(false);

  // Check device type on component mount and route changes
  useEffect(() => {
    const checkDevice = () => {
      const shouldRedirect = DeviceDetection.shouldRedirectToApp();
      
      // Define allowed paths for mobile users (landing page and footer links only)
      const allowedMobilePaths = [
        '/landing',            // Landing/marketing page - mobile default
        '/privacy-policy',     // Privacy Policy
        '/terms-of-use',      // Terms of Use
        '/about',             // About Us
        '/contact',           // Contact
        '/do-not-sell',       // Do Not Sell My Data
        '/ccpa',              // CCPA Rights
        '/email-consent',     // Email consent page for post-registration
        '/devices'            // Devices page - app download links
      ];
      
      // Define content paths that should show mobile detection page (not redirect)
      const contentPaths = [
        '/watch',             // Watch pages
        '/movie',             // Movie PDP
        '/series',            // Series PDP
        '/live-tv'            // Live TV
      ];
      
      // Check if current path is allowed for mobile users
      const currentPath = location.pathname;
      const isMobileAllowedPath = allowedMobilePaths.includes(currentPath);
      
      // Check if current path is a content path (should show detection page)
      const isContentPath = contentPaths.some(path => currentPath.startsWith(path));
      
      // Determine mobile behavior:
      // - Allowed paths: normal access
      // - Content paths: show mobile detection page
      // - Other paths (homepage, etc.): redirect to landing
      const shouldRedirectMobile = shouldRedirect && !isMobileAllowedPath;
      const shouldShowDetectionPage = shouldRedirectMobile && isContentPath;
      const shouldRedirectToLanding = shouldRedirectMobile && !isContentPath;

      // Check if device should show smart banner (mobile/tablet but not redirect)
      const isMobile = DeviceDetection.isMobile();
      const isIPad = DeviceDetection.isIPad();
      const isAndroidTablet =
        /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);
      const shouldShowBanner = (isMobile || isIPad || isAndroidTablet) && !shouldRedirectMobile;

      setIsMobileDevice(shouldShowDetectionPage);
      setShowSmartBanner(shouldShowBanner);
      setDeviceCheckComplete(true);
      
      // Handle redirect to landing for non-content pages
      // Use sessionStorage to prevent redirect loops
      // BUT: Don't redirect if there's a channel parameter (deep link)
      if (shouldRedirectToLanding) {
        const hasChannelParam = location.search.includes('channel=');
        const hasRedirected = sessionStorage.getItem('mobileRedirectedToLanding');
        
        if (!hasChannelParam && !hasRedirected) {
          sessionStorage.setItem('mobileRedirectedToLanding', 'true');
          window.location.href = '/landing';
        } else if (hasChannelParam) {
          // If there's a channel parameter, show the mobile detection page instead
          // This allows the deep link to work through the mobile intercept
          setIsMobileDevice(true);
        } else {
          // If already redirected once, show detection page instead of looping
          setIsMobileDevice(true);
        }
      }
    };

    // Run check immediately
    checkDevice();

    // Also run check on window resize (orientation change)
    const handleResize = () => {
      setTimeout(checkDevice, 100); // Small delay to ensure accurate measurements
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [location.pathname]); // Add location.pathname as dependency

  // Show loading while device check is in progress
  if (!deviceCheckComplete) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'var(--color-electric-bg)',
          backgroundImage: 'radial-gradient(ellipse at top, rgba(47,110,176,0.25), transparent 60%)',
          color: 'var(--color-electric-contrast)',
          fontFamily: 'var(--font-family-primary)',
          letterSpacing: '0.04em',
        }}
      >
        <div>Loading ElectricNOW...</div>
      </div>
    );
  }

  // Show mobile detection page only for content pages
  if (isMobileDevice) {
    return <MobileIncompatiblePage currentPath={location.pathname} />;
  }

  // Normal app for desktop/tablet users with smart banner
  return (
    <div className="App">
      {/* Smart App Banner for mobile/tablet users */}
      {showSmartBanner && <SmartAppBanner currentPath={location.pathname} />}

      <AuthWrapper>
        <ProfileProvider>
          <AppLoadProvider>
            <ProfileRedirectHandler>
              <LandingRedirectHandler>
                <Routes>
                  {/* Well-known files for app deep linking */}
                  <Route
                    path="/.well-known/apple-app-site-association"
                    element={<AppleAppSiteAssociation />}
                  />
                  <Route path="/.well-known/assetlinks.json" element={<AndroidAssetLinks />} />

                  {/* Ads.txt for programmatic advertising */}
                  <Route path="/ads.txt" element={<AdsTxt />} />

                  <Route path="/" element={<HomepageRedirect />} />
                  <Route path="/english" element={<MoviesPage />} />
                  <Route path="/espanol" element={<SeriesPage />} />
                  <Route path="/homepage" element={<Homepage />} />
                  <Route path="/landing" element={<Landing />} />
                  <Route
                    path="/profiles"
                    element={
                      <ProtectedRoute>
                        <ProfileSelectionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/movies" element={<MoviesPage />} />
                  <Route path="/series" element={<SeriesPage />} />
                  <Route path="/live-tv" element={<LiveTVPage />} />
                  <Route path="/live-tv/:channelSlug" element={<LiveTVPage />} />
                  <Route path="/watch/:channelSlug" element={<WatchPageWrapper />} />
                  <Route path="/watch/:channelSlug/:episodeId" element={<WatchPageWrapper />} />
                  <Route path="/trailer/:channelSlug/:trailerVideoId" element={<TrailerWatchPage />} />
                  <Route path="/movie/:movieId" element={<MoviePDPWrapper />} />
                  <Route path="/movie/:videoSlug" element={<MoviePDPWrapper />} />
                  <Route path="/series/:seriesId" element={<SeriesPDPWrapper />} />
                  <Route path="/series/:videoSlug" element={<SeriesPDPWrapper />} />
                  <Route path="/category/:categorySlug" element={<CategoryPageWrapper />} />
                  <Route path="/search" element={<SearchPageWrapper />} />
                  <Route
                    path="/my-lists"
                    element={
                      <ProtectedRoute>
                        <MyListsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-lists/:tab"
                    element={
                      <ProtectedRoute>
                        <MyListsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-streams"
                    element={
                      <ProtectedRoute>
                        <MyStreamsWrapper />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-stream"
                    element={
                      <ProtectedRoute>
                        <CreateStreamWrapper />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <StreamingDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Legal and Support Pages */}
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                  <Route path="/ccpa" element={<CCPAPage />} />
                  <Route path="/do-not-sell" element={<DoNotSellPage />} />

                  {/* Email Consent Page for Post-Registration */}
                  <Route path="/email-consent" element={<EmailConsentPage />} />

                  {/* Vibe Coding Story Page */}
                  <Route path="/vibe-coding-story" element={<VibeCodingStoryPage />} />

                  {/* Devices Page */}
                  <Route path="/devices" element={<DevicesPage />} />

                  {/* Checkout Page - Protected Route */}
                  <Route
                    path="/checkout/:channelId"
                    element={
                      <ProtectedRoute>
                        <CheckoutPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Payment Profile Page - Protected Route */}
                  <Route
                    path="/account/payment"
                    element={
                      <ProtectedRoute>
                        <PaymentProfilePage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Transaction History Page - Protected Route */}
                  <Route
                    path="/account/transactions"
                    element={
                      <ProtectedRoute>
                        <TransactionHistoryPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Catch-all route - must be last */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </LandingRedirectHandler>
            </ProfileRedirectHandler>
          </AppLoadProvider>
        </ProfileProvider>
      </AuthWrapper>
    </div>
  );
}

export default App;
